import { useDebounceFn } from "@vueuse/core";
import { buildClassifier, type ClassifyResult, type PatternEntry, PII_ALLOW, PII_BLOCK, PII_WARN, type Span } from "~vue/utils/pii/keywordClassifier";
import { computed, type ComputedRef, type Ref, ref, watch } from "vue";

export interface PiiDetection {
    piiResult: Ref<ClassifyResult | null>;
    piiBlocked: ComputedRef<boolean>;
    piiWarning: ComputedRef<boolean>;
    piiDetected: ComputedRef<boolean>;
    acceptRisk: () => void;
    piiBannerMessage: ComputedRef<string>;
    piiMessage: ComputedRef<string>;
    warnSpans: ComputedRef<Span[]>;
    errorSpans: ComputedRef<Span[]>;
}

const DEBOUNCE_MS = 300;

export function usePiiDetection(patterns: PatternEntry[] | null | undefined, textContent: Ref<string>): PiiDetection {
    const piiResult = ref<ClassifyResult | null>(null);
    const riskAccepted = ref(false);
    let previousReason = "";

    // Build classifier once if patterns are provided
    const classify = patterns?.length ? buildClassifier(patterns) : null;

    const runClassification = useDebounceFn(() => {
        if (!classify || !textContent.value.trim()) {
            piiResult.value = null;
            return;
        }
        piiResult.value = classify(textContent.value);
    }, DEBOUNCE_MS);

    // Fire-and-forget: result is written to the piiResult ref, so watchers react automatically
    watch(textContent, () => {
        void runClassification();
    });

    // Reset riskAccepted when the detected PII types change
    watch(piiResult, (newResult) => {
        const newReason = newResult?.reason ?? "";
        if (newReason !== previousReason) {
            riskAccepted.value = false;
            previousReason = newReason;
        }
    });

    const piiBlocked = computed(() => piiResult.value?.score === PII_BLOCK);
    const piiWarning = computed(() => piiResult.value?.score === PII_WARN && !riskAccepted.value);
    const piiDetected = computed(() => (piiResult.value?.score ?? PII_ALLOW) > PII_ALLOW);

    // Informative banner shown while typing (no action buttons)
    const piiBannerMessage = computed(() => {
        if (!piiResult.value || piiResult.value.score === PII_ALLOW) {
            return "";
        }
        if (piiResult.value.score === PII_BLOCK) {
            return "Your message appears to contain sensitive information that cannot be sent.";
        }
        const reason = piiResult.value.reason;
        if (reason.includes("url")) {
            return "Nadia is unable to access the URLs shared for your security.";
        }
        return "Your message may contain sensitive information.";
    });

    // Modal message shown when user attempts to send
    const piiMessage = computed(() => {
        if (!piiResult.value || piiResult.value.score === PII_ALLOW) {
            return "";
        }
        if (piiResult.value.score === PII_BLOCK) {
            return "Your message contains sensitive information. Please remove it before sending.";
        }
        const reason = piiResult.value.reason;
        if (reason.includes("url")) {
            return "Nadia is unable to access the URLs shared for your security.";
        }
        return "Your message may contain sensitive information. Are you sure you want to send it?";
    });

    const warnSpans = computed(() => piiResult.value?.warn_spans ?? []);
    const errorSpans = computed(() => piiResult.value?.error_spans ?? []);

    const acceptRisk = () => {
        riskAccepted.value = true;
    };

    return {
        piiResult,
        piiBlocked,
        piiWarning,
        piiDetected,
        acceptRisk,
        piiBannerMessage,
        piiMessage,
        warnSpans,
        errorSpans,
    };
}
